import React, { useCallback, useState } from 'react';
import { Box, Button, VisuallyHidden } from '@indeed/ifl-components';
import { ChevronRight } from '@indeed/ifl-icons';
import { ScreenerQuestionLabel } from './ScreenerQuestionLabel';

interface ProgressiveAutomationApplicationButtonProps {
    applicationDraftId: string;
    ariaLabel: string;
    disabled: boolean;
    onClick: () => void;
    children: React.ReactNode;
}

export const ProgressiveAutomationApplicationButton: React.FC<
    ProgressiveAutomationApplicationButtonProps
> = ({ applicationDraftId, ariaLabel, disabled, onClick, children }) => {
    const [screenerQuestionLabel, setScreenerQuestionLabel] = useState<string | null>(null);
    const handleScreenerQuestionLabelChange = useCallback(
        (loadedApplicationDraftId: string, label: string | null): void => {
            if (loadedApplicationDraftId === applicationDraftId) {
                setScreenerQuestionLabel(label);
            }
        },
        [applicationDraftId],
    );

    return (
        <>
            <Button
                type="button"
                variant="text"
                onClick={onClick}
                aria-label={ariaLabel}
                disabled={disabled}
                sx={{
                    inlineSize: '100%',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingBlock: 0,
                    paddingInline: 0,
                    textAlign: 'start',
                }}
            >
                <Box sx={{ flex: 1, paddingInlineEnd: 2 }}>
                    {children}
                    <ScreenerQuestionLabel
                        applicationDraftId={applicationDraftId}
                        onLabelChange={handleScreenerQuestionLabelChange}
                    />
                </Box>
                <ChevronRight size="md" color="foreground.strong" aria-hidden />
            </Button>
            {/* The visible label loads inside the button and is hidden from
                assistive technology. This sibling live region announces the
                label when its query resolves without changing the button's name. */}
            <VisuallyHidden role="status">{screenerQuestionLabel ?? ''}</VisuallyHidden>
        </>
    );
};
